'use client'

import { type FC } from 'react'

import {
  ResponsiveImage,
  Select,
  SelectOption,
  SlideToggle,
  TypeStyleHeadlineExtraExtraSmall,
  TypeStyleHeadlineExtraSmall,
} from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../_providers/TranslationProvider.client'

import * as Styles from './sizeChartComponents.styles'
import {
  MEASURE_IMAGE_ASPECT_RATIO,
  MEASURE_IMAGE_WIDTH_PX,
  type SizeChartSummaryEntry,
} from './sizeChartData'

// Unit abbreviations are not localized
const UNIT_TOGGLE_LABELS = [{ label: 'IN' }, { label: 'CM' }]

export type SizeChartUnitToggleProps = {
  onToggleUnit: () => void
  showMetric: boolean
}

/** Switches every measurement on a size chart between inches and centimeters. */
export const SizeChartUnitToggle: FC<SizeChartUnitToggleProps> = ({ onToggleUnit, showMetric }) => {
  const {
    sizesSection: { sizeChart: strings },
  } = useTranslation()

  return (
    <SlideToggle
      checked={showMetric}
      groupLabel={strings.unitToggleLabel}
      labels={UNIT_TOGGLE_LABELS}
      onChange={onToggleUnit}
    />
  )
}

export type SizeChartSizeSelectProps = {
  onChange: (size: string) => void
  sizes: readonly string[]
  value: string
}

/** Picks the size a chart highlights, and that the measurement summary describes. */
export const SizeChartSizeSelect: FC<SizeChartSizeSelectProps> = ({ onChange, sizes, value }) => {
  const { sizesSection } = useTranslation()

  return (
    <div>
      <Select
        isInvalid={false}
        label={<Styles.SizeSelectLabel>{sizesSection.title}</Styles.SizeSelectLabel>}
        value={value}
        onChange={onChange}
        renderSelectValue={selected => selected || sizesSection.dropdownPlaceholder}
      >
        {sizes.map(size => (
          <SelectOption key={size} value={size}>
            {size}
          </SelectOption>
        ))}
      </Select>
    </div>
  )
}

export type SizeChartMeasurementSummaryProps = {
  entries: readonly SizeChartSummaryEntry[]
}

/** Restates the selected size's row as a scannable card above the grid it came from. */
export const SizeChartMeasurementSummary: FC<SizeChartMeasurementSummaryProps> = ({ entries }) => {
  if (entries.length === 0) return null

  return (
    <Styles.MeasurementSummary>
      {entries.map(entry => (
        <Styles.MeasurementSummaryEntry key={entry.label}>
          <Styles.MeasurementSummaryLabel asTag='dt' isUppercase textTransform='uppercase'>
            {entry.label}
          </Styles.MeasurementSummaryLabel>
          <Styles.MeasurementSummaryValue asTag='dd'>{entry.value}</Styles.MeasurementSummaryValue>
        </Styles.MeasurementSummaryEntry>
      ))}
    </Styles.MeasurementSummary>
  )
}

export type MeasureTip = {
  image?: string | null
  kind: string | null
  tip: string | null
}

/** The same tip can be repeated across sizing tables; the list should only show it once. */
export const dedupeMeasureTips = (
  tables: ReadonlyArray<{ measureTips?: readonly MeasureTip[] | null }>
): MeasureTip[] => {
  const byKey = new Map<string, MeasureTip>()
  for (const tip of tables.flatMap(table => table.measureTips ?? [])) {
    const key = `${tip.kind}-${tip.tip}`
    if (!byKey.has(key)) byKey.set(key, tip)
  }
  return [...byKey.values()]
}

export type SizeChartHowToMeasureProps = {
  measureTips: readonly MeasureTip[] | null | undefined
}

export const SizeChartHowToMeasure: FC<SizeChartHowToMeasureProps> = ({ measureTips }) => {
  const {
    sizesSection: { sizeChart: strings },
  } = useTranslation()

  if (!measureTips?.length) return null

  return (
    <>
      <TypeStyleHeadlineExtraSmall asTag='h2'>{strings.howToMeasure}</TypeStyleHeadlineExtraSmall>
      <Styles.HowToMeasureList>
        {measureTips.map(tip => (
          <Styles.HowToMeasureItem key={`${tip.kind}-${tip.tip}`}>
            {tip.image ? (
              <Styles.MeasureImageFrame>
                <ResponsiveImage
                  // The tip kind and copy alongside the diagram already describe it.
                  alt=''
                  aria-hidden='true'
                  aspectRatios={{
                    sm: MEASURE_IMAGE_ASPECT_RATIO,
                    md: MEASURE_IMAGE_ASPECT_RATIO,
                  }}
                  loading='lazy'
                  src={tip.image}
                  widths={{
                    unit: 'px',
                    sm: MEASURE_IMAGE_WIDTH_PX,
                    md: MEASURE_IMAGE_WIDTH_PX,
                  }}
                />
              </Styles.MeasureImageFrame>
            ) : null}
            <Styles.MeasureText>
              <TypeStyleHeadlineExtraExtraSmall asTag='p'>
                {tip.kind}
              </TypeStyleHeadlineExtraExtraSmall>
              <Styles.MeasureTipText asTag='p'>{tip.tip}</Styles.MeasureTipText>
            </Styles.MeasureText>
          </Styles.HowToMeasureItem>
        ))}
      </Styles.HowToMeasureList>
    </>
  )
}
